/**
 * The public library: free to walk into. Shelves of real books (public domain: Equiano's own story, Austen, Fitzgerald,
 * Conan Doyle, Carroll, Sun Tzu, Marcus Aurelius, Gibran; and two short stories of the game's own), a reading table,
 * and armchairs at a tall window with the rain on it. Reading passes time (by the pages), lifts your mood, and teaches
 * you a little the first time each day; your place in each book is kept, and finishing one is part of your story.
 */
import { advance, type LifeEvent, type LifeState } from './life.ts';

const LIBRARIES: Record<string, string> = {
  lagos: 'Lagos City Library, Yaba', us: 'Lenox Reading Room, Manhattan', france: 'Bibliothèque des Pluies, Paris',
  china: 'Bund Reading House, Shanghai', japan: 'Amagumo Library, Tokyo', italy: 'Biblioteca del Portico, Rome', uae: 'Al Qasr Reading Hall, Dubai',
};
export const libraryFor = (region: string): string => LIBRARIES[region] ?? LIBRARIES.lagos;

/** where you are in a book: the chapter, and how far through it (0..1) */
export interface Bookmark { ch: number; at: number }
interface LibraryState { marks?: Record<string, Bookmark>; finished?: string[]; learnedDay?: number; visits?: number; pages?: number }
const of = (s: LifeState): LibraryState => ((s as LifeState & { library?: LibraryState }).library ??= {});
const today = (s: LifeState) => Math.floor(s.minutes / 1440);

export const bookmarkOf = (s: LifeState, book: string): Bookmark | null => of(s).marks?.[book] ?? null;
export const finishedBooks = (s: LifeState): string[] => of(s).finished ?? [];

/** in through the door (a first visit is remembered) */
export function enterLibrary(s: LifeState) {
  const l = of(s);
  l.visits = (l.visits ?? 0) + 1;
  if (l.visits === 1) s.history.push({ t: s.minutes, text: 'Joined the library.' });
}

/**
 * A sitting with a book: `pages` turned, ending at `mark` (`done`: the last page of the last chapter). Two minutes a
 * page, fun for it, a little Academics the first sitting of the day (a real read: five pages or more).
 */
export function readBook(s: LifeState, book: string, title: string, pages: number, mark: Bookmark, done: boolean): { line: string; events: LifeEvent[] } {
  const l = of(s);
  (l.marks ??= {})[book] = mark;
  l.pages = (l.pages ?? 0) + pages;
  s.needs.fun = Math.min(100, s.needs.fun + Math.min(12, pages * 0.8));
  let learned = false;
  if (pages >= 5 && l.learnedDay !== today(s)) { l.learnedDay = today(s); s.skills.academics = Math.min(100, s.skills.academics + 0.5); learned = true; }
  const fin = done && !(l.finished ?? []).includes(book);
  if (fin) { (l.finished ??= []).push(book); s.history.push({ t: s.minutes, text: `Finished reading ${title}.` }); }
  const mins = Math.max(1, Math.round(pages * 2));
  const line = fin ? `The last page of ${title}. You sit with it a moment before you close it.`
    : pages < 1 ? 'You close the book. Your place is kept.'
    : `${pages} page${pages === 1 ? '' : 's'} of ${title} (${mins} minutes). Your place is kept${learned ? '; you learned something today' : ''}.`;
  return { line, events: advance(s, mins, { busy: true }) };
}
